/**
 * LPC 50% deposit payment schedule experiment (payments_lpc_50_percent_deposit_schedule).
 *
 * Trigger: Reaching checkout with an LPC offer that has a template attached, order > $1,999 (AU).
 * Exclude: Orders with LPC offers that do not have a template attached.
 * - Control: no payment schedule UI (schedules suppressed).
 * - Variant (on): only 50% static template (when offer already has a schedule).
 */
import { createSelector } from 'reselect'
import { isSpoofed } from 'selectors/isSpoofed'
import {
  findPostPurchaseCheckout,
  isBedbankItem,
  isInstantBookingLEHotelItem,
  isLEHotelItem,
  isTourV2Item,
} from 'lib/checkout/checkoutUtils'
import { LPC_50_PERCENT_DEPOSIT_MINIMUM_OFFER_VALUE } from 'constants/payment'
import getPayableTotal from '../getPayableTotal'
import isPaymentScheduleEnabled from './isPaymentScheduleEnabled'
import { isLpcHotelOffer } from 'lib/offer/offerTypes'
import { getExperimentEnabledDecision, getOptimizelyExperimentVariation } from 'lib/optimizely/optimizelyUtils'
import { Experiments } from 'constants/experiments'
import { isExperimentPreview } from 'lib/experiments/preview'

const LPC_50_EXPERIMENT_ID = Experiments.paymentsLpc50PercentDepositSchedule.id

function hasLpcItemPaymentScheduleTemplate(
  item: App.Checkout.LEHotelItem,
  offers: Record<string, App.AnyOffer>,
): boolean {
  const offer = offers[item.offerId] as App.HotelOffer | undefined
  if (!isInstantBookingLEHotelItem(item) || !isLpcHotelOffer(offer)) {
    return false
  }
  const info = item.paymentScheduleInfo ?? offer?.paymentScheduleInfo
  return !!(info?.staticSchedule?.templateId ?? info?.dynamicSchedule?.templateId)
}

/**
 * Carts that match the experiment trigger: LPC offer with template attached, order > $1,999 (AU).
 * Excludes tour/bedbank and LPC orders that do not have a template attached.
 */
export const isLpc50DepositExperimentTriggerCart = createSelector(
  (state: App.State) => isSpoofed(state),
  (state: App.State) => state.geo.currentRegionCode,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => isPaymentScheduleEnabled(state),
  (state: App.State) => getPayableTotal(state),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => state.offer.offers,
  (spoofed, regionCode, postPurchase, paymentScheduleEnabled, payableTotal, cartItems, offers): boolean => {
    // Keep spoof users out of this experiment so they follow standard payment schedule behavior.
    if (spoofed || regionCode !== 'AU' || postPurchase || !paymentScheduleEnabled) {
      return false
    }
    if (payableTotal < LPC_50_PERCENT_DEPOSIT_MINIMUM_OFFER_VALUE) {
      return false
    }
    if (cartItems.some(isTourV2Item) || cartItems.some(isBedbankItem)) {
      return false
    }
    const hotelItems = cartItems.filter(isLEHotelItem)
    if (hotelItems.length === 0) {
      return false
    }
    const allInstantLpc = hotelItems.every(item => {
      const offer = offers[item.offerId] as App.HotelOffer | undefined
      return isInstantBookingLEHotelItem(item) && isLpcHotelOffer(offer)
    })
    if (!allInstantLpc) {
      return false
    }
    return hotelItems.some(item => hasLpcItemPaymentScheduleTemplate(item, offers))
  },
)

/**
 * Bucketed control: hide all payment schedules for trigger carts.
 * Uses variationKey from the experiment object directly — getOptimizelyExperimentVariation
 * returns undefined for control because enabled is false for that arm.
 */
export const isLpc50DepositExperimentControlSuppressesSchedules = createSelector(
  isLpc50DepositExperimentTriggerCart,
  (state: App.State) =>
    isExperimentPreview(state) ? undefined : state.optimizely.optimizelyExperiments[LPC_50_EXPERIMENT_ID]?.variationKey,
  (trigger, variationKey): boolean => {
    return trigger && (variationKey?.toLowerCase() === 'control')
  },
)

/** Variant on: show only 50% static schedule (no dynamic) when offer already has a template. */
export const isLpc50DepositExperimentVariantForces50Schedule = createSelector(
  isLpc50DepositExperimentTriggerCart,
  (state: App.State) =>
    getOptimizelyExperimentVariation(state, LPC_50_EXPERIMENT_ID),
  (trigger, variation): boolean => trigger && getExperimentEnabledDecision(variation),
)
